import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ChevronLeft } from 'lucide-react';
import { apiFetch } from '../utils/api';
import WorkoutDayEditor from '../components/WorkoutDayEditor';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

const CoachCreateWorkoutPage = () => {
  const pathParts = window.location.pathname.split('/');
  const athleteId = pathParts[3];
  const isEdit = pathParts[4] === 'workout' && pathParts[6] === 'edit';
  const workoutId = isEdit ? pathParts[5] : null;

  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [athleteName, setAthleteName] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [days, setDays] = useState([
    { day_number: 1, name: i18n.t('workout.day_default_name', { number: 1 }), exercises: [] }
  ]);
  const [nameError, setNameError] = useState(false);

  useEffect(() => {
    if (isEdit) fetchWorkout();
    fetchAthleteStats();
  }, []);

  const fetchAthleteStats = async () => {
    try {
      const res = await apiFetch(`/api/coach/athlete/${athleteId}/stats`);
      if (res.ok) {
        const data = await res.json();
        setAthleteName(data.name || '');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchWorkout = async () => {
    try {
      const res = await apiFetch(`/api/coach/athlete/${athleteId}/workouts/${workoutId}`);
      if (res.ok) {
        const data = await res.json();
        setName(data.name);
        setDescription(data.description || '');
        setDays(data.days || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const addDay = () =>
    setDays(prev => [
      ...prev,
      { day_number: prev.length + 1, name: i18n.t('workout.day_default_name', { number: prev.length + 1 }), exercises: [] }
    ]);

  const removeDay = (dayIndex) =>
    setDays(prev => prev.filter((_, i) => i !== dayIndex));

  const updateDayName = (dayIndex, value) =>
    setDays(prev => {
      const updated = [...prev];
      updated[dayIndex] = { ...updated[dayIndex], name: value };
      return updated;
    });

  const addExercise = (dayIndex) =>
    setDays(prev => {
      const updated = [...prev];
      updated[dayIndex] = {
        ...updated[dayIndex],
        exercises: [...updated[dayIndex].exercises, { name: '', sets: '', notes: '' }]
      };
      return updated;
    });

  const updateExercise = (dayIndex, exIndex, field, value) =>
    setDays(prev => {
      const updated = [...prev];
      const exercises = [...updated[dayIndex].exercises];
      exercises[exIndex] = { ...exercises[exIndex], [field]: value };
      updated[dayIndex] = { ...updated[dayIndex], exercises };
      return updated;
    });

  const removeExercise = (dayIndex, exIndex) =>
    setDays(prev => {
      const updated = [...prev];
      updated[dayIndex] = {
        ...updated[dayIndex],
        exercises: updated[dayIndex].exercises.filter((_, i) => i !== exIndex)
      };
      return updated;
    });

  const handleSubmit = async () => {
    if (!name.trim()) { setNameError(true); return; }
    setNameError(false);
    setSaving(true);
    try {
      const endpoint = isEdit
        ? `/api/coach/athlete/${athleteId}/workouts/${workoutId}`
        : `/api/coach/athlete/${athleteId}/workouts`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await apiFetch(endpoint, {
        method,
        body: JSON.stringify({ name, description, days })
      });

      if (res.ok) {
        navigate(`/coach/athlete/${athleteId}`);
      } else {
        alert(t('workout.error_saving'));
      }
    } catch (err) {
      console.error(err);
      alert(t('common.error_connection'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading"><div className="loading-spinner" /></div>;

  return (
    <div className="page-container animate-fade-in">
      <div className="workout-page-header">
        <button className="workout-page-back" onClick={() => navigate(-1)}>
          <ChevronLeft size={14} />
          {t('common.back')}
        </button>
        <h1 className="header-title">{isEdit ? t('workout.edit_routine') : t('workout.new_routine')}</h1>
        {athleteName && (
          <p className="text-muted" style={{ fontSize: '13px', marginTop: '4px' }}>
            {t('workout.for_athlete', { name: athleteName })}
          </p>
        )}
      </div>

      <div className="workout-info-card">
        <div className="input-group">
          <label className="input-label">{t('workout.routine_name_label')}</label>
          <input
            type="text"
            className="input"
            placeholder={t('workout.routine_name_placeholder')}
            value={name}
            onChange={(e) => { setName(e.target.value); if (nameError) setNameError(false); }}
            style={nameError ? { borderColor: 'var(--destructive)' } : {}}
          />
          {nameError && (
            <p style={{ color: 'var(--destructive)', fontSize: '12px', marginTop: '6px' }}>
              {t('workout.name_required')}
            </p>
          )}
        </div>
        <div className="input-group" style={{ marginBottom: 0 }}>
          <label className="input-label">
            {t('workout.description_label')}{' '}
            <span style={{ color: 'var(--muted-foreground)', fontWeight: 400, textTransform: 'none' }}>
              {t('common.optional')}
            </span>
          </label>
          <input
            type="text"
            className="input"
            placeholder={t('workout.description_placeholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>

      <p className="days-section-title">{t('workout.training_days')}</p>

      {days.map((day, dayIndex) => (
        <WorkoutDayEditor
          key={dayIndex}
          day={day}
          dayIndex={dayIndex}
          totalDays={days.length}
          onUpdateName={updateDayName}
          onAddExercise={addExercise}
          onUpdateExercise={updateExercise}
          onRemoveExercise={removeExercise}
          onRemoveDay={removeDay}
        />
      ))}

      <button className="btn-add-day" onClick={addDay}>
        <Plus size={16} />
        {t('workout.add_day')}
      </button>

      <div className="save-section">
        <button
          className="btn btn-primary w-full"
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving
            ? <div className="loading-spinner" style={{ width: '20px', height: '20px' }} />
            : (isEdit ? t('workout.update_btn') : t('workout.create_btn'))
          }
        </button>
      </div>
    </div>
  );
};

export default CoachCreateWorkoutPage;
